import { createContext, useContext, useEffect, useEffectEvent, useRef } from 'react';

/** Scoped to background content; foreground dialogs and tickets keep the default active state. */
export const BackgroundWorkContext = createContext(true);

/**
 * Runs `onResume` when background work goes from paused back to active — never on mount,
 * and never on the way into a pause.
 *
 * Restoring a suspended `refetchInterval` only schedules the *next* fetch, so a query whose
 * poll was gated off comes back holding whatever it had when the overlay opened and waits a
 * full interval before correcting it. Pollers use this to catch up on the poll they missed.
 */
export function useOnBackgroundResume(onResume: () => void): void {
    useOnResume(useContext(BackgroundWorkContext), onResume);
}

/** `useOnBackgroundResume` for any pause, e.g. the background's combined with a section's own on-screen state. */
export function useOnResume(active: boolean, onResume: () => void): void {
    const wasPausedRef = useRef(false);
    const run = useEffectEvent(onResume);

    useEffect(() => {
        if (!active) {
            wasPausedRef.current = true;
            return;
        }
        if (!wasPausedRef.current) return;
        wasPausedRef.current = false;
        run();
    }, [active]);
}

/**
 * Whether a poll that was suspended for a pause actually missed a tick, given when the query
 * last got data. Refetching only then keeps a dismissed-after-a-second overlay from firing a
 * burst of requests it doesn't need, while still closing the stale window when the pause
 * really did outlast the cadence.
 */
export function missedPollWhilePaused(dataUpdatedAt: number, intervalMs: number | false | undefined): boolean {
    if (!intervalMs) return false;
    return Date.now() - dataUpdatedAt >= intervalMs;
}
